// Main Cashier เขียนผล reduce ของ tableStatesEvents ลง node เดิมให้แอปอื่นอ่าน (projection)
//
//   RTDB stores/{id}/tableState      ← mobile-order-flutter, xtable-moible-webapp, mobile report
//   Firestore sync_data/table_state  ← หน้ารายการโต๊ะของ Flutter
//
// POS เองไม่อ่าน node พวกนี้แล้ว — reduce จาก event เอง (ดู stores/tableStatesEvents.ts)
// เครื่องอื่น (2nd / terminal) ไม่เขียน: ค่าเดียวกันอยู่แล้ว เขียนหลายเครื่องแค่เปลือง bandwidth

import _ from "lodash";
import { set } from "firebase/database";
import { setDoc } from "firebase/firestore";
import type { PiniaPluginContext } from "pinia";
import { RunMode } from "~~/interface/Pos";
import { FIRESTORE_COLLECTIONS } from "~~/interface/FirestoreCollections";
import { reportLiveSyncError } from "~~/modules/core/utils/liveSyncStatus";
import { isDevReadOnly } from "~/modules/core/utils/devStoreOverride";
import { isLiveSyncSuspended } from "../composables/useTableSync";

const PROJECTION_DEBOUNCE_MS = 500;

function TableStateProjectionPlugin({ store }: PiniaPluginContext) {
  if (store.$id !== "tableStatesEvents") return;

  let lastJson: string | null = null;

  const project = _.debounce(() => {
    if (useRunningMode().getMode() !== RunMode.MAIN_CASHIER) return;
    // ยังโหลดชุดแรกไม่ครบ / เครื่องหมายรอบไม่ตรงกับ OPEN ใน jobchain (jobchain ยังโหลดไม่ถึง หรือเครื่องค้างรอบเก่า)
    // = state ยังไม่จริง เขียนไปจะทับ tableState ของรอบปัจจุบัน (ดู tableStatesEvents.syncLoadedFlag)
    if (!store.$state.projectionReady) return;
    // dev สวมร้านอื่น / หลังปิดร้านก่อน reload (เหตุผลดู liveSyncSuspended ใน useTableSync.ts)
    if (isDevReadOnly() || isLiveSyncSuspended()) return;

    const tableState = usePosStore().tableState || {};
    const json = JSON.stringify(tableState);
    if (json === lastJson) return;
    lastJson = json;

    const fb = useFirebase();
    Promise.all([
      // set ทั้ง node — โต๊ะที่ CLEAR แล้วหลุดออกเอง
      set(fb.tableStateRef(), tableState),
      setDoc(
        useFirebaseFirestore().subCollectionDocIdRef(FIRESTORE_COLLECTIONS.SYNC_DATA, FIRESTORE_COLLECTIONS.TABLE_STATE),
        tableState
      ),
    ]).catch((error) => {
      lastJson = null; // รอบหน้าเขียนใหม่
      reportLiveSyncError("tableStateProjection", error);
    });
  }, PROJECTION_DEBOUNCE_MS);

  // revision เพิ่มทุกครั้งที่ reduce ใหม่
  store.$subscribe(() => project());
}

export default defineNuxtPlugin(({ $pinia }) => {
  $pinia.use(TableStateProjectionPlugin);
});
